import { CreateFilterItemsProps } from '@app/components/organisms/filter-items';
import { SearchFilters } from '@app/models';

/**
 * Handles the change event for a checkbox group filter that allows multiple selections.
 * Updates the filter by adding or removing the itemValue from the current string array.
 */
export const handleMultipleCheckboxGroupValueChange = (
    filterKey: keyof SearchFilters,
    currentFilterValues: string[] | undefined,
    itemValue: string | number,
    isChecked: boolean,
    onFilterChangeCallback: CreateFilterItemsProps['onFilterChange'],
): void => {
    const newValues = getNewMultipleCheckboxGroupValue(currentFilterValues, itemValue, isChecked);
    onFilterChangeCallback(filterKey, newValues);
};

/**
 * Get new value for checkbox group
 */
export const getNewMultipleCheckboxGroupValue = (
    currentFilterValues: string[] | undefined,
    itemValue: string | number,
    isChecked: boolean,
): string[] => {
    const actualCurrentValues = currentFilterValues || [];
    const itemValueStr = String(itemValue);

    return isChecked
        ? actualCurrentValues.includes(itemValueStr)
            ? actualCurrentValues
            : [...actualCurrentValues, itemValueStr]
        : actualCurrentValues.filter(v => v !== itemValueStr);
};

/**
 * Handles the change event for a hierarchical selection (e.g., rating).
 * In hierarchical selection, selecting a value affects all values above it.
 * - If an item is unchecked, it becomes the new active selection.
 * - If the currently active selection is checked, all filters are cleared.
 * - If a non-active (but visually selected due to hierarchy) item is unchecked,
 *   that item's level becomes the new active selection.
 */
export const handleHierarchicalSelectionChange = (
    filterKey: keyof SearchFilters,
    clickedValue: string | number,
    isChecked: boolean,
    currentSelection: number | null | undefined,
    onFilterChangeCallback: CreateFilterItemsProps['onFilterChange'],
): void => {
    const newValue = getNewHierarchicalSelectionValue(clickedValue, isChecked, currentSelection);

    onFilterChangeCallback(filterKey, newValue);
};

/**
 * Get new value for hierarchical selection
 */
export const getNewHierarchicalSelectionValue = (
    clickedValue: string | number,
    isChecked: boolean,
    currentSelection: number | null | undefined,
): number | null => {
    const numericValue = Number(clickedValue);
    const shouldClear = !isChecked && currentSelection === numericValue;

    return shouldClear ? null : numericValue;
};

/**
 *
 * @param filterKey Handles single checkbox toggle
 * @param isChecked
 * @param onFilterChangeCallback
 */
export const handleCheckboxChange = (
    filterKey: keyof SearchFilters,
    value: string,
    isChecked: boolean,
    onFilterChangeCallback: CreateFilterItemsProps['onFilterChange'],
): void => {
    onFilterChangeCallback(filterKey, isChecked ? value : null);
};
